/* TBS Flame Wrap v3 - vanilla port of Canvas UI "Flame Wrap" (canvasui.dev/docs/components/flame-wrap)
   Scoped for Tuned By Shawn bundle badges. Defines <tbs-flame-wrap>.
   Flame-only overlay mode: badge stays real HTML, WebGL2 canvas draws the
   fire around its rect. Respects prefers-reduced-motion, pauses offscreen,
   caps live WebGL contexts at 4 per page. */
(() => {
  if (customElements.get('tbs-flame-wrap')) return;

  const VERT = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUv;
void main () { vUv = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

  const FRAG = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec2 uRectCenter;
uniform vec2 uRectHalf;
uniform float uCorner;
uniform vec3 uColor;
uniform float uIntensity;
uniform float uHeight;
uniform float uSpread;
uniform float uScale;
uniform float uTurbulence;
uniform float uTurbScale;
uniform float uSparks;
uniform float uSparkSize;
uniform float uSparkDensity;
uniform float uSparkSpeed;
uniform float uRim;
uniform float uMelt;
uniform float uSmoke;
#define S(a, b, t) smoothstep(a, b, t)
vec3 permute (vec3 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }
float snoise (vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m; m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}
float fbm (vec2 p) {
  mat2 m = mat2(0.8, -0.6, 0.6, 0.8);
  float v = 0.5 * snoise(p);
  p = m * p * 2.03 + vec2(11.3, 7.1);
  v += 0.27 * snoise(p);
  p = m * p * 1.97 + vec2(3.7, 19.1);
  v += 0.15 * snoise(p);
  p = m * p * 2.01 + vec2(8.3, 2.9);
  v += 0.08 * snoise(p);
  return v * 0.5 + 0.5;
}
float fbm2 (vec2 p) {
  float v = 0.62 * snoise(p);
  v += 0.31 * snoise(mat2(0.8, -0.6, 0.6, 0.8) * p * 2.13 + vec2(5.2, 1.3));
  return v * 0.54 + 0.5;
}
vec2 turbulence (vec2 p) {
  float freq = 12.0 * clamp(uScale, 0.05, 1.0) * clamp(uTurbScale, 0.2, 3.0);
  mat2 rot = mat2(0.6, -0.8, 0.8, 0.6);
  for (float i = 0.0; i < 7.0; i++) {
    float phase = freq * (p * rot).y + 6.0 * uTime + i;
    p += uTurbulence * rot[0] * sin(phase) / freq;
    rot *= mat2(0.6, -0.8, 0.8, 0.6);
    freq *= 1.2;
  }
  return p;
}
vec3 hash3 (vec2 p) {
  vec3 q = vec3(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)), dot(p, vec2(419.2, 371.9)));
  return fract(sin(q) * 43758.5453);
}
float sdRoundRect (vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}
void main () {
  vec2 frag = vUv * uResolution;
  vec2 rel = frag - uRectCenter;
  float unit = max(uHeight, 24.0);
  float corner = min(uCorner, min(uRectHalf.x, uRectHalf.y));
  float spreadPx = max(uSpread, 8.0);
  float t = uTime;
  float detail = clamp(uScale, 0.05, 1.0);
  float d0 = sdRoundRect(rel, uRectHalf, corner);
  float px = rel.x / unit;
  float py = rel.y / unit;
  float yA = max(rel.y - uRectHalf.y, 0.0) / unit;
  float sway = snoise(vec2(px * 1.1, t * 0.5)) * 0.55 + snoise(vec2(px * 2.4, t * 0.9 + 41.0)) * 0.25;
  float sx = px + yA * sway;
  float env = fbm2(vec2(sx * 1.6 * detail + 3.7, t * 0.55 - yA * 0.4));
  float env2 = fbm2(vec2(sx * 3.6 * detail, t * 0.85 + 17.0 - yA * 0.6));
  float tongue = clamp(0.75 * S(0.3, 0.9, env) + 0.5 * S(0.4, 0.95, env2), 0.0, 1.0);
  float meltPx = max(uMelt, 1.0);
  float biteTop = (3.0 + meltPx * 1.4) * (0.35 + 0.65 * tongue) + 2.0 * snoise(vec2(px * 5.0 * detail, t * 1.1 + 5.0));
  float yF = uRectHalf.y - biteTop;
  float frontTop = rel.y - yF;
  float perim = fbm2(rel * (1.9 / unit) * detail + vec2(0.0, t * 0.4) + 31.0);
  float biteSB = 3.0 + meltPx * (0.25 + 0.75 * perim);
  float frontSB = d0 + biteSB;
  float wTop = S(-0.62 * unit, -0.1 * unit, rel.y - uRectHalf.y) * S(10.0, -30.0, abs(rel.x) - (uRectHalf.x - corner));
  float front = mix(frontSB, frontTop, wTop);
  float reach = mix(spreadPx * 0.9, unit * (0.2 + 0.45 * tongue), wTop);
  float q = front / reach;
  vec2 np = vec2(px * 2.3, py * 1.25 - t * 1.85) * detail;
  np = turbulence(np);
  float n = fbm(np);
  float win = S(-0.08, 0.02, q);
  float root = exp(-abs(q) * 5.0);
  float ridge = 1.0 - abs(2.0 * n - 1.0);
  float flameH = mix(1.0, 0.5 + 0.6 * tongue, wTop);
  float g = max(q, 0.0) / flameH;
  float shred = fbm2(np * 1.9 + 63.0);
  g *= 1.0 + 0.7 * (shred - 0.5) * S(0.2, 0.8, g);
  float dens = n * 0.95 + ridge * 0.45 - 0.18 + (1.0 - min(g, 1.0)) * 0.3 - g * (0.9 + 0.25 * n);
  dens = clamp(dens * 2.4, 0.0, 1.0) * win;
  dens *= mix(1.0 - S(0.32, 1.05, q), 1.0 - S(0.9, 1.2, g), wTop);
  float body = dens * dens * (3.0 - 2.0 * dens);
  float emis = clamp(uIntensity, 0.0, 2.0);
  float e = body * (0.55 + 0.75 * root) * (0.45 + 0.55 * n) + win * root * (0.1 + 0.4 * n);
  e *= mix(0.45, 1.0, wTop) * max(emis, 0.001);
  vec3 hot = mix(uColor, vec3(1.0), 0.35);
  vec3 deep = mix(uColor, uColor * uColor, 0.5) * 0.9;
  float ramp = 1.0 - exp(-e * 2.4);
  vec3 fireCol = mix(deep, uColor, S(0.0, 0.55, ramp));
  float core = ramp * (0.45 + 0.55 * exp(-g * 2.2)) * (0.5 + 0.5 * n);
  fireCol = mix(fireCol, hot, S(0.7, 1.05, core));
  fireCol *= 0.8 + 0.4 * ramp;
  float fireA = clamp(1.0 - exp(-e * 3.4), 0.0, 1.0);
  float halo = exp(-max(front, 0.0) / (spreadPx * 1.2)) * S(0.0, 3.0, front) * (0.5 + 0.5 * n) * 0.3 * clamp(uRim, 0.0, 2.0) * mix(1.0, 0.45, wTop);
  vec3 glow = uColor * halo * clamp(uIntensity, 0.0, 2.0);
  if (uSparks > 0.001) {
    float sSpeed = max(uSparkSpeed, 0.05);
    float sCells = 5.0 * clamp(uSparkDensity, 0.3, 2.5);
    float sSize = clamp(uSparkSize, 0.2, 3.0);
    float gate = S(-0.05, 0.1, q) * (1.0 - S(1.3, 2.2, q)) * wTop;
    float spark = 0.0;
    for (float L = 0.0; L < 2.0; L++) {
      float speed = 1.5 * sSpeed * (0.75 + 0.5 * L);
      vec2 ps = vec2(px, py - t * speed);
      ps.x += 0.08 * snoise(vec2(py * 0.9 + L * 5.0, t * 0.5));
      float cells = sCells * (1.0 + 0.6 * L);
      vec2 cl = floor(ps * cells) + L * 19.0;
      vec2 fr = fract(ps * cells);
      vec3 rnd = hash3(cl);
      vec3 rnd2 = hash3(cl + 7.3);
      float on = step(rnd2.x, 0.42);
      float life = fract(rnd.z + t * sSpeed * (0.3 + 0.5 * rnd2.x));
      vec2 ppos = vec2(0.5) + 0.56 * (rnd.xy - 0.5);
      ppos.x += 0.14 * sin(t * (0.7 + rnd.z * 2.8) + rnd.y * 6.2832) + 0.1 * snoise(vec2(t * 0.6 + rnd.x * 9.0, cl.y * 0.7)) + (life - 0.5) * 0.5 * (rnd2.y - 0.5);
      ppos.y += (life - 0.5) * 0.3 * rnd2.y;
      float tw = S(0.02, 0.2, life) * S(1.0, 0.55, life);
      tw *= 0.75 + 0.25 * sin(t * (6.0 + rnd2.z * 9.0) + rnd.x * 6.2832);
      vec2 pd = (fr - ppos) / cells * unit;
      pd.y *= 0.55 + 0.3 * rnd2.z;
      float dp = length(pd);
      float r = (0.004 + 0.014 * rnd.y * rnd.y) * unit * sSize * mix(1.15, 0.55, life);
      float bmask = S(0.5, 0.32, max(abs(fr.x - 0.5), abs(fr.y - 0.5)));
      float sbody = exp(-dp * dp / (r * r));
      float sbloom = exp(-dp * dp / (r * r * 6.0)) * 0.3;
      spark += (sbody + sbloom) * tw * tw * on * bmask * (1.0 - 0.35 * L);
    }
    spark *= gate * uSparks;
    fireCol += mix(uColor, vec3(1.0), 0.55) * spark * 1.6;
    fireA = clamp(fireA + spark * 0.85, 0.0, 1.0);
  }
  vec2 edgePx = min(frag, uResolution - frag);
  float fadeW = max(24.0, spreadPx * 0.75);
  float fade = S(0.0, fadeW, edgePx.x) * S(0.0, fadeW, edgePx.y);
  fireA *= fade;
  glow *= fade;
  halo *= fade;
  float wisp = S(0.45, 0.9, fbm2(np * 0.55 + vec2(0.0, 17.0)));
  float smoke = S(1.55, 1.05, g) * S(0.85, 1.15, g) * (1.0 - body) * wTop * wisp * 0.055 * clamp(uSmoke, 0.0, 2.0) * fade;
  vec3 smokeCol = mix(vec3(0.5), uColor, 0.5);
  float sA = clamp(smoke, 0.0, 1.0);
  float a = clamp(fireA + sA * (1.0 - fireA), 0.0, 1.0);
  outColor = vec4(fireCol * fireA + glow + smokeCol * sA * (1.0 - fireA), clamp(a + halo * 0.6, 0.0, 1.0));
}`;

  const DEFAULTS = {
    intensity: 0.9, height: 30, spread: 6, radius: 4, speed: 0.35,
    scale: 0.85, turbulence: 0.5, turbulenceScale: 0.6,
    sparks: 1.2, sparkSize: 0.5, sparkDensity: 1, sparkSpeed: 1,
    rim: 2, melt: 2, smoke: 0.8
  };

  let liveContexts = 0;
  const MAX_CONTEXTS = 4;

  function parseColorAttr(raw) {
    if (!raw) return null;
    raw = raw.trim();
    const hex = raw.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
      let h = hex[1];
      if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      return [
        parseInt(h.slice(0, 2), 16) / 255,
        parseInt(h.slice(2, 4), 16) / 255,
        parseInt(h.slice(4, 6), 16) / 255
      ];
    }
    const parts = raw.split(/[\s,\/]+/).map(Number).filter((v) => !isNaN(v));
    if (parts.length === 3) {
      if (parts.some((v) => v > 1)) {
        return parts.map((v) => Math.min(255, Math.max(0, v)) / 255);
      }
      return parts.map((v) => Math.min(1, Math.max(0, v)));
    }
    return null;
  }

  function parseColor(el) {
    try {
      const bg = getComputedStyle(el).backgroundColor;
      const m = bg && bg.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
      if (m) {
        let r = parseInt(m[1], 10) / 255;
        let g = parseInt(m[2], 10) / 255;
        let b = parseInt(m[3], 10) / 255;
        if (r + g + b > 0.15) {
          return [Math.min(1, r * 1.05), g * 0.7, b * 0.15];
        }
      }
    } catch (e) {}
    return [1.0, 0.45, 0.08];
  }

  function createFlame(host, badge, output, config) {
    const gl = output.getContext('webgl2', {
      alpha: true, depth: false, stencil: false,
      antialias: false, premultipliedAlpha: true
    });
    if (!gl || gl.isContextLost()) return null;

    function compile(type, text) {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, text);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('TbsFlameWrap shader error:', gl.getShaderInfoLog(shader));
      }
      return shader;
    }
    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = compile(gl.FRAGMENT_SHADER, FRAG);
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;

    const uniforms = {};
    const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < count; i++) {
      const info = gl.getActiveUniform(program, i);
      uniforms[info.name] = gl.getUniformLocation(program, info.name);
    }
    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const rect = { cx: 0, cy: 0, hx: 1, hy: 1 };
    let dpr = 1;

    function syncSize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(output.clientWidth * dpr));
      const h = Math.max(1, Math.round(output.clientHeight * dpr));
      if (output.width !== w || output.height !== h) {
        output.width = w; output.height = h;
      }
      const oR = output.getBoundingClientRect();
      const bR = badge.getBoundingClientRect();
      if (oR.width > 0 && bR.width > 0) {
        rect.cx = (bR.left + bR.right) / 2 - oR.left;
        rect.cy = oR.bottom - (bR.top + bR.bottom) / 2;
        rect.hx = bR.width / 2;
        rect.hy = bR.height / 2;
      }
    }
    syncSize();

    let time = 0;
    function render() {
      gl.useProgram(program);
      gl.uniform2f(uniforms.uResolution, output.width, output.height);
      gl.uniform1f(uniforms.uTime, time);
      gl.uniform2f(uniforms.uRectCenter, rect.cx * dpr, rect.cy * dpr);
      gl.uniform2f(uniforms.uRectHalf, Math.max(rect.hx * dpr, 1), Math.max(rect.hy * dpr, 1));
      gl.uniform1f(uniforms.uCorner, Math.max(config.radius, 0) * dpr);
      gl.uniform3f(uniforms.uColor, config.color[0], config.color[1], config.color[2]);
      gl.uniform1f(uniforms.uIntensity, Math.max(config.intensity, 0));
      gl.uniform1f(uniforms.uHeight, Math.max(config.height, 24) * dpr);
      gl.uniform1f(uniforms.uSpread, Math.max(config.spread, 8) * dpr);
      gl.uniform1f(uniforms.uScale, Math.max(config.scale, 0.05));
      gl.uniform1f(uniforms.uTurbulence, Math.max(config.turbulence, 0));
      gl.uniform1f(uniforms.uTurbScale, Math.max(config.turbulenceScale, 0.2));
      gl.uniform1f(uniforms.uSparks, Math.max(config.sparks, 0));
      gl.uniform1f(uniforms.uSparkSize, Math.max(config.sparkSize, 0.2));
      gl.uniform1f(uniforms.uSparkDensity, Math.max(config.sparkDensity, 0.3));
      gl.uniform1f(uniforms.uSparkSpeed, Math.max(config.sparkSpeed, 0.05));
      gl.uniform1f(uniforms.uRim, Math.max(config.rim, 0));
      gl.uniform1f(uniforms.uMelt, Math.max(config.melt, 0) * dpr);
      gl.uniform1f(uniforms.uSmoke, Math.max(config.smoke, 0));
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, output.width, output.height);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    let raf = 0;
    let lastTime = performance.now();
    let destroyed = false;
    let running = false;
    let visible = true;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let reducedMotion = motionQuery.matches;

    function frame(now) {
      if (destroyed) return;
      if (!visible) { running = false; return; }
      const delta = Math.min((now - lastTime) / 1000, 1 / 30);
      lastTime = now;
      if (!reducedMotion) time += delta * config.speed;
      render();
      if (reducedMotion) { running = false; return; }
      raf = requestAnimationFrame(frame);
    }
    function start() {
      if (destroyed || running || !visible) return;
      running = true;
      lastTime = performance.now();
      raf = requestAnimationFrame(frame);
    }
    start();

    function onMotionChange() { reducedMotion = motionQuery.matches; start(); }
    motionQuery.addEventListener('change', onMotionChange);

    const ro = new ResizeObserver(() => { syncSize(); start(); });
    ro.observe(output);
    ro.observe(badge);

    const io = new IntersectionObserver((entries) => {
      visible = entries[entries.length - 1] ? entries[entries.length - 1].isIntersecting : true;
      if (visible) start();
    });
    io.observe(host);

    output.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      destroyed = true;
      cancelAnimationFrame(raf);
    });

    return {
      resize() { syncSize(); start(); },
      destroy() {
        destroyed = true;
        cancelAnimationFrame(raf);
        ro.disconnect();
        io.disconnect();
        motionQuery.removeEventListener('change', onMotionChange);
        gl.deleteProgram(program);
        gl.deleteShader(vs);
        gl.deleteShader(fs);
        gl.deleteBuffer(quad);
      }
    };
  }

  class TbsFlameWrap extends HTMLElement {
    connectedCallback() {
      if (this._instance || this._tried) return;
      this._tried = true;
      if (liveContexts >= MAX_CONTEXTS) return;
      const targetSel = this.getAttribute('data-target');
      const badge = (targetSel && this.querySelector(targetSel)) || this.firstElementChild;
      if (!badge) return;

      const config = Object.assign({}, DEFAULTS);
      for (const key of Object.keys(DEFAULTS)) {
        const attr = 'data-' + key.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
        const raw = this.getAttribute(attr);
        if (raw !== null && raw !== '' && !isNaN(parseFloat(raw))) {
          config[key] = parseFloat(raw);
        }
      }
      config.color = parseColorAttr(this.getAttribute('data-color')) || parseColor(badge);

      if (getComputedStyle(this).position === 'static') {
        this.style.position = 'relative';
      }
      const reach = Math.round(Math.max(config.height, 24) * 1.5) + 40;
      const glow = Math.round(Math.max(config.spread, 8) * 3) + 16;
      const output = document.createElement('canvas');
      output.setAttribute('aria-hidden', 'true');
      output.style.cssText =
        'position:absolute;top:-' + reach + 'px;right:-' + glow + 'px;bottom:-' + glow +
        'px;left:-' + glow + 'px;width:calc(100% + ' + (glow * 2) + 'px);height:calc(100% + ' +
        (reach + glow) + 'px);pointer-events:none;';
      this.appendChild(output);

      const instance = createFlame(this, badge, output, config);
      if (instance) {
        this._instance = instance;
        liveContexts++;
      } else {
        output.remove();
      }
    }
    disconnectedCallback() {
      if (this._instance) {
        this._instance.destroy();
        this._instance = null;
        liveContexts = Math.max(0, liveContexts - 1);
      }
      this._tried = false;
    }
  }
  customElements.define('tbs-flame-wrap', TbsFlameWrap);
})();